import {Box, Button, Divider, List, ListItem, Stack, Typography} from '@mui/material';
import React, {Fragment} from 'react';

import {
	AlertInfo,
	CTAButtonFixed,
	FailToCompleteActionModal,
	MainFooter,
} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import {HolderDataCollapse} from './components/HolderDataCollapse';
import {useFinished} from './hooks/useFinished';
import {ProceduresContainer, StyledFinishedContainer} from './styles';
import {FinishedProps} from './types';

const finishedLabels = translations['pt-br'].privateFlow.refund.previewServiceType.finished;
const backToRefundMenuLabel = translations['pt-br'].privateFlow.refund.conclusionModal.buttonText;

export const Finished = ({onBackToRefundMenu}: FinishedProps) => {
	const {
		isMobile,
		holderData,
		exportLoading,
		handleExportClick,
		errorModalOpen,
		handleCloseErrorModal,
		isExitWarningModalOpen,
		setIsExitWarningModalOpen,
		handleBackToRefundMenuClick,
		handleConfirmExit,
		handleCancelExit,
		requestNumber,
		protocolNumber,
		estimatedValue,
		selectedProcedureDescription,
		procedureSectionLabel,
		pdfFileName,
	} = useFinished(onBackToRefundMenu);

	return (
		<Fragment>
			<FailToCompleteActionModal
				reversedFooter
				open={errorModalOpen}
				setOpen={(value) => { if (!value) handleCloseErrorModal(); }}
				headerTitle="Erro"
				title="Não foi possível prosseguir com a solicitação"
				description="Um erro ocorreu no processamento. Por favor, tente novamente em alguns instantes."
				primaryActionText="Tentar novamente"
				handleTryAgain={handleExportClick}
				secondaryActionText="Fechar"
				fallbackAction={handleCloseErrorModal}
			/>
			<FailToCompleteActionModal
				reversedFooter
				open={isExitWarningModalOpen}
				setOpen={(value) => setIsExitWarningModalOpen(value)}
				headerTitle="Aviso"
				title="Deseja realmente sair?"
				description="O download do arquivo será interrompido, mas você poderá acessar novamente pelo menu consulta da prévia de reembolso."
				primaryActionText="Confirmar"
				handleTryAgain={handleConfirmExit}
				secondaryActionText="Cancelar"
				fallbackAction={handleCancelExit}
			/>
			<AlertInfo.Root severity="success">
				<AlertInfo.Description>
					<Typography fontWeight="600">{finishedLabels.successAlert}</Typography>
				</AlertInfo.Description>
			</AlertInfo.Root>
			<StyledFinishedContainer>
				<Stack gap={2} sx={{order: {xs: 2, sm: 'unset'}}}>
					<Typography fontWeight="600" color="text.secondary" variant="subtitle2">
						{finishedLabels.requestSectionTitle}
					</Typography>
					<ProceduresContainer>
						<AlertInfo.Root
							sx={{borderBottomRightRadius: 0, borderBottomLeftRadius: 0}}
							severity="info">
							<AlertInfo.Description>
								{finishedLabels.valueEstimateAlert}
							</AlertInfo.Description>
						</AlertInfo.Root>
						<Stack
							direction={{xs: 'column', sm: 'row'}}
							px={2}
							width="100%"
							justifyContent="space-between"
							gap={{xs: 2, sm: 0}}>
							<Stack gap={1} width="100%">
								<Typography variant="body2" color="text.secondary" fontWeight={600}>
									{finishedLabels.requestPreviewLabel}
								</Typography>
								<Typography variant="body1" color="text.primary">
									Nº {requestNumber}
								</Typography>
							</Stack>
							<Stack gap={1} width="100%">
								<Typography variant="body2" color="text.secondary" fontWeight={600}>
									{finishedLabels.protocolLabel}
								</Typography>
								<Typography variant="body1" color="text.primary">
									Nº {protocolNumber}
								</Typography>
							</Stack>
						</Stack>
						<Divider />
						<Stack
							direction={{xs: 'column', sm: 'row'}}
							px={2}
							width="100%"
							paddingBottom="16px"
							justifyContent="space-between"
							gap={{xs: 2, sm: 0}}>
							<Stack gap={1} width="100%">
								<Typography variant="body2" color="text.secondary" fontWeight={600}>
									{procedureSectionLabel}
								</Typography>
								<Typography variant="body1" color="text.primary">
									{selectedProcedureDescription}
								</Typography>
							</Stack>
							<Stack gap={1} width="100%">
								<Typography variant="body2" color="text.secondary" fontWeight={600}>
									{finishedLabels.estimatedValueLabel}
								</Typography>
								<Typography variant="h5" fontWeight={600} color="text.primary">
									{estimatedValue}
								</Typography>
							</Stack>
						</Stack>
					</ProceduresContainer>
					<Typography fontWeight="600" color="text.secondary" variant="subtitle2">
						{finishedLabels.downloadSectionTitle}
					</Typography>

					<Stack justifyContent="flex-end" alignItems="flex-end" gap={2}>
						<ProceduresContainer>
							<Stack
								direction={{xs: 'column', sm: 'row'}}
								padding="24px 16px"
								alignItems={{xs: 'stretch', sm: 'center'}}
								justifyContent="space-between"
								gap={{xs: 1, sm: 0}}>
							<Typography variant="subtitle1" color="text.secondary">
								{pdfFileName}
							</Typography>
								<Button
									color="primary"
									variant="text"
									disabled={exportLoading}
									onClick={handleExportClick}
									endIcon={
										exportLoading ? (
											<Loading size={12} color="inherit" />
										) : (
											<Icon name="download" size={16} />
										)
									}>
									{finishedLabels.exportButton}
								</Button>
							</Stack>
						</ProceduresContainer>
					<Button
						variant="contained"
						color="primary"
						onClick={handleBackToRefundMenuClick}
						sx={{display: {xs: 'none', sm: 'inline-flex'}}}>
						{backToRefundMenuLabel}
					</Button>
					</Stack>
				</Stack>
				{isMobile ? (
					<Box sx={{order: {xs: 1, sm: 'unset'}}}>
						<HolderDataCollapse holderData={holderData} />
					</Box>
				) : (
					<ProceduresContainer>
						<List disablePadding sx={{padding: '16px', gap: '16px'}}>
							{holderData.map(({labelKey, content}, index) => (
								<Fragment key={labelKey}>
									<ListItem>
										<Stack gap={0.5} width="100%">
											<Typography
												variant="body2"
												color="text.secondary"
												fontWeight={600}>
												{finishedLabels[labelKey]}
											</Typography>
											<Typography variant="body1" color="text.primary">
												{content}
											</Typography>
										</Stack>
									</ListItem>
									{index < holderData.length - 1 && <Divider />}
								</Fragment>
							))}
						</List>
					</ProceduresContainer>
				)}
			</StyledFinishedContainer>
			<Box sx={{display: {xs: 'block', sm: 'none'}}}>
				<CTAButtonFixed.Root>
				<CTAButtonFixed.Action
					variant="contained"
					color="primary"
					onClick={handleBackToRefundMenuClick}>
					{backToRefundMenuLabel}
				</CTAButtonFixed.Action>
				</CTAButtonFixed.Root>
			</Box>
			<MainFooter />
		</Fragment>
	);
};
